// src/pages/Inscription/InscriptionForm.jsx
import React, { useState } from "react";
import { createActeurByUser } from "./../../services/acteursServices";
import { sendNotificationToAdmin } from "../../services/notificationServices";
import { toast } from "react-toastify";
import Button from "./../../components/Button/Button";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import { useUser } from "./../../contexts/UserContext";
import { FaUserPlus } from "react-icons/fa";

const InscriptionForm = ({ onActorCreated }) => {
  const { user } = useUser();
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    tel: "",
    adresse: "",
    cp: "",
    commune: "",
    genre: "",
    date_naissance: "",
    photo_url: "",
    club_acteur: "",
    licence_number: "",
    groupe_souhaite: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Vous devez être connecté pour créer un acteur.");
      return;
    }

    try {
      const acteurData = { ...formData, user_id: user.userId };
      await createActeurByUser(acteurData, token);

      const message = `${user.nom} ${user.prenom} a créé un nouvel acteur : ${formData.nom} ${formData.prenom} / ${formData.club_acteur} / ${formData.groupe_souhaite}`;
      await sendNotificationToAdmin(message, user.id);

      toast.success("Acteur créé avec succès");

      setFormData({
        nom: "",
        prenom: "",
        email: "",
        tel: "",
        adresse: "",
        cp: "",
        commune: "",
        genre: "",
        date_naissance: "",
        photo_url: "",
        club_acteur: "",
        licence_number: "",
        groupe_souhaite: "",
      });

      onActorCreated && onActorCreated();
    } catch (error) {
      console.error("Erreur lors de la création de l'acteur :", error);
      toast.error("Erreur lors de la création de l'acteur");
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/80 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70 transition";

  const labelClass =
    "block text-xs font-semibold tracking-wide uppercase text-slate-700 dark:text-slate-200 mb-1.5";

  return (
    <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-xl overflow-hidden">
      {/* Bandeau haut */}
      <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />

      <div className="p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold tracking-[0.18em] uppercase text-primary">
              Créer un nouvel acteur
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Complétez les informations pour déclarer un acteur du Judo
              départemental.
            </p>
          </div>
          <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <FaUserPlus />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Identité */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 dark:text-slate-300">
              Identité
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="nom" className={labelClass}>
                  Nom *
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="prenom" className={labelClass}>
                  Prénom *
                </label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="genre" className={labelClass}>
                  Genre *
                </label>
                <select
                  id="genre"
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                >
                  <option value="">Sélectionnez</option>
                  <option value="Masculin">Masculin</option>
                  <option value="Féminin">Féminin</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="date_naissance" className={labelClass}>
                  Date de naissance
                </label>
                <input
                  type="date"
                  id="date_naissance"
                  name="date_naissance"
                  value={formData.date_naissance}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Coordonnées */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 dark:text-slate-300">
              Coordonnées
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="email" className={labelClass}>
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label htmlFor="tel" className={labelClass}>
                  Téléphone *
                </label>
                <input
                  type="tel"
                  id="tel"
                  name="tel"
                  value={formData.tel}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="adresse" className={labelClass}>
                Adresse *
              </label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleInputChange}
                className={inputClass}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="cp" className={labelClass}>
                  Code postal *
                </label>
                <input
                  type="text"
                  id="cp"
                  name="cp"
                  value={formData.cp}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="commune" className={labelClass}>
                  Commune *
                </label>
                <input
                  type="text"
                  id="commune"
                  name="commune"
                  value={formData.commune}
                  onChange={handleInputChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </div>

          {/* Club & Judo */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold tracking-[0.18em] uppercase text-slate-500 dark:text-slate-300">
              Club & informations Judo
            </h3>

            <div>
              <label htmlFor="club_acteur" className={labelClass}>
                Nom du club
              </label>
              <input
                type="text"
                id="club_acteur"
                name="club_acteur"
                value={formData.club_acteur}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="licence_number" className={labelClass}>
                  Numéro de licence
                </label>
                <input
                  type="text"
                  id="licence_number"
                  name="licence_number"
                  value={formData.licence_number}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="photo_url" className={labelClass}>
                  URL de la photo
                </label>
                <input
                  type="url"
                  id="photo_url"
                  name="photo_url"
                  value={formData.photo_url}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="https://..."
                />
              </div>
            </div>

            <InfoBubble title="Les différents groupes possibles">
              <p>✅ Responsable de club</p>
              <p>✅ Enseignant</p>
              <p>✅ Membre de Commission</p>
              <p>✅ Membre du Comité</p>
              <p>✅ Bénévolontaire</p>
              <p>✅ Arbitre (titulaire, stagiaire, école d'arbitrage)</p>
              <p>
                ✅ Commissaire Sportif (titulaire, stagiaire, école d'arbitrage)
              </p>
            </InfoBubble>

            <div>
              <label htmlFor="groupe_souhaite" className={labelClass}>
                Choix de groupe(s)
              </label>
              <textarea
                id="groupe_souhaite"
                name="groupe_souhaite"
                value={formData.groupe_souhaite}
                onChange={handleInputChange}
                className={inputClass + " min-h-[90px] resize-y"}
                placeholder="Indiquez vos choix de groupe(s) ici (ex : Enseignant, Arbitre stagiaire, Membre Commission Arbitrage...)"
              />
            </div>
          </div>

          {/* Bouton */}
          <div className="pt-2">
            <Button
              type="submit"
              text="Créer un nouvel acteur"
              icon={FaUserPlus}
              className="w-full justify-center gap-2"
            />
            <p className="mt-2 text-[11px] text-center text-slate-500 dark:text-slate-400">
              Les champs marqués d’un * sont obligatoires.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InscriptionForm;
